// src/components/comenzi/List.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Badge,
  Form,
  InputGroup,
  Card,
  Modal,
  Tabs,
  Tab
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import StocCurentPickerModal from "../pickers/StocCurentPickerModal";
import ManoperaPickerModal from "../pickers/ManoperaPickerModal";
import ClientOrderSummaryTable from "./ClientOrderSummaryTable";
import { applyLaborNorm } from "./manoperaCalculation";
import LaborEmployees, { LaborEmployeeSummary } from "./LaborEmployees";

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

function formatDateTimeLocal(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ClientOrderLinesList() {
  const [showOrderLines, setShowOrderLines] = useState(false);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statuses, setStatuses] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Generating status trackers
  const [generatingId, setGeneratingId] = useState(null);
  const [generatingProdId, setGeneratingProdId] = useState(null);

  // Modal for internal order details & editing
  const [selectedInternalOrder, setSelectedInternalOrder] = useState(null);
  const [formOrder, setFormOrder] = useState(null);
  const [savingInternalOrder, setSavingInternalOrder] = useState(false);
  const [isScading, setIsScading] = useState(false);
  const [modalTab, setModalTab] = useState("general");
  const [partPickerOpen, setPartPickerOpen] = useState(false);
  const [stocPickerOpen, setStocPickerOpen] = useState(false);
  const [activeRecipeLineIdx, setActiveRecipeLineIdx] = useState(null);
  const [ums, setUms] = useState([]);
  const [loadingInternalOrder, setLoadingInternalOrder] = useState(false);
  const [showInternalOrderModal, setShowInternalOrderModal] = useState(false);
  const [appVariant, setAppVariant] = useState("STANDARD");
  const [tarifSkill1, setTarifSkill1] = useState(0.0);
  const [tarifSkill2, setTarifSkill2] = useState(0.0);
  const [manoperaPickerOpen, setManoperaPickerOpen] = useState(false);
  const [activeManoperaLineIdx, setActiveManoperaLineIdx] = useState(null);

  // Load status and UMs nomenclature
  useEffect(() => {
    async function loadStatuses() {
      try {
        const data = await authFetch("/comenzi/statusuri");
        if (Array.isArray(data)) {
          setStatuses(data);
        }
      } catch (err) {
        console.error("Eroare la încărcarea nomenclatorului de statusuri:", err);
      }
    }
    async function loadUms() {
      try {
        const data = await authFetch("/bxums?limit=500");
        if (data && Array.isArray(data.items)) {
          setUms(data.items);
        }
      } catch (err) {
        console.error("Eroare la încărcarea nomenclatorului de unități de măsură:", err);
      }
    }
    async function loadFirma() {
      try {
        const res = await authFetch("/firma");
        if (res && res.items && res.items.length > 0) {
          setAppVariant(res.items[0].app_variant || "STANDARD");
          setTarifSkill1(res.items[0].tarif_skill1 || 0.0);
          setTarifSkill2(res.items[0].tarif_skill2 || 0.0);
        }
      } catch (err) {
        console.error("Eroare la încărcarea variantei aplicației și tarifelor:", err);
      }
    }
    loadStatuses();
    loadUms();
    loadFirma();
  }, []);

  // Fetch orders list
  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const skip = currentPage * itemsPerPage;
      const usp = new URLSearchParams();
      if (search.trim()) usp.set("search", search.trim());
      if (statusFilter) usp.set("status_id", statusFilter);
      usp.set("skip", String(skip));
      usp.set("limit", String(itemsPerPage));

      const path = `/comenzi/client/${showOrderLines ? "linii" : "sumar"}?${usp.toString()}`;
      const data = await authFetch(path);

      if (data && Array.isArray(data.items)) {
        setItems(data.items);
        setTotalCount(data.total);
      } else {
        setItems([]);
        setTotalCount(0);
      }
    } catch (e) {
      setError(e.message || "Eroare la preluarea liniilor de comandă.");
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch on filter or page change
  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
  }, [currentPage, itemsPerPage, statusFilter, showOrderLines]);

  // Restabiliți scrollbar-ul pe body când se închid picker-ele imbricate
  useEffect(() => {
    if (showInternalOrderModal) {
      const checkAndRestore = () => {
        if (showInternalOrderModal && !document.body.classList.contains("modal-open")) {
          document.body.classList.add("modal-open");
        }
      };
      const t = setTimeout(checkAndRestore, 300);
      const interval = setInterval(checkAndRestore, 1000);
      return () => {
        clearTimeout(t);
        clearInterval(interval);
      };
    }
  }, [showInternalOrderModal, stocPickerOpen, manoperaPickerOpen]);

  // Handle Search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchList();
  };

  // Handle Clear filters
  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setCurrentPage(0);
  };

  // Trigger internal order generation (Simaus Logic)
  const handleGenerateInternalOrder = async (lineId) => {
    if (!window.confirm("Sigur doriți să generați Devizul / Comanda internă pentru această linie?")) return;
    setGeneratingId(lineId);
    try {
      const result = await authFetch(`/comenzi/client/linie/${lineId}/genereaza-interna`, { method: "POST" });
      toast.success(
        <div>
          Devizul intern <strong>{result.bxnumar_comanda}</strong> a fost generat!
        </div>
      );
      fetchList();
    } catch (err) {
      toast.error(err.message || "Eroare la generare Deviz.");
    } finally {
      setGeneratingId(null);
    }
  };

  const handleGenerateProdOrder = async (lineId) => {
    if (!window.confirm("Sigur doriți să generați Comanda de Producție (cu rețetă) pentru această linie?")) return;
    setGeneratingProdId(lineId);
    try {
      const result = await authFetch(`/comenzi-prod/generate-from-client-line/${lineId}`, { method: "POST" });
      toast.success(
        <div>
          Comanda Producție <strong>{result.bxnumar_comanda}</strong> a fost generată!
        </div>
      );
      fetchList();
    } catch (err) {
      toast.error(err.message || "Eroare la generare Comandă Producție.");
    } finally {
      setGeneratingProdId(null);
    }
  };

  // View internal order details in modal
  const handleViewInternalOrder = async (internalOrderId) => {
    setLoadingInternalOrder(true);
    setShowInternalOrderModal(true);
    setModalTab("general");
    try {
      const data = await authFetch(`/comenzi/interna/${internalOrderId}`);
      setSelectedInternalOrder(data);
      setFormOrder(data);
    } catch (err) {
      toast.error(err.message || "Eroare la încărcarea detaliilor de comandă internă.");
      setShowInternalOrderModal(false);
    } finally {
      setLoadingInternalOrder(false);
    }
  };

  // Header field change
  const handleHeaderChange = (field, val) => {
    setFormOrder(prev => {
      const updated = { ...prev, [field]: val };
      if (field === "bxcantitate" || field === "bxprice") {
        const cant = Number(updated.bxcantitate) || 0;
        const price = Number(updated.bxprice) || 0;
        updated.bxlvalue = cant * price;
      }
      return updated;
    });
  };

  // Labor row handlers
  const handleAddManopera = () => {
    const newRow = {
      bxdata_manopera: new Date().toISOString(),
      bxqty_manopera: 1.0,
      bxqty_manopera1: 0.0,
      bxpret_manopera: 0.0,
      bxtotal_manopera: 0.0,
      bxspecificatii: "",
      bxcentrucost_id: formOrder?.centrucost_id || 1,
      bxmanoperacod_id: 1,
      bxunitate_id: formOrder?.bxunitate_id || 1,
      bxtip: 1,
      bxtiptarif: 1,
      bxremiza: 0.0,
      qty_executat: 0
    };
    setFormOrder(prev => ({
      ...prev,
      manopere: [...(prev.manopere || []), newRow]
    }));
  };

  const handleUpdateManopera = (index, field, val) => {
    setFormOrder(prev => {
      const list = [...(prev.manopere || [])];
      const item = { ...list[index], [field]: val };
      if (field === "bxqty_manopera" || field === "bxpret_manopera") {
        const qty = Number(item.bxqty_manopera) || 0;
        const pret = Number(item.bxpret_manopera) || 0;
        item.bxtotal_manopera = qty * pret;
      }
      list[index] = item;
      return { ...prev, manopere: list };
    });
  };

  const handleRemoveManopera = (index) => {
    setFormOrder(prev => {
      const list = [...(prev.manopere || [])];
      list.splice(index, 1);
      return { ...prev, manopere: list };
    });
  };

  // Recipe row handlers
  const handleAddRecipeLine = (rectype = 1) => {
    const newRow = {
      bxum_id: ums[0]?.id || 1,
      bxpart_id: 0,
      part_code: "",
      part_name: "",
      bxnorma: 1.0,
      deluat: rectype === 11 ? 1.0 : 0.0,
      luat: 0.0,
      bxrectype: rectype,
      bxegal: 1,
      bxrestqty: rectype === 11 ? 1.0 : 0.0,
      pretvanzare: 0.0,
      qty_proforma: 0.0,
      bxremiza: 0.0,
      inchid_fortat: 0,
      data_scadere: new Date().toISOString().split('T')[0]
    };
    setFormOrder(prev => ({
      ...prev,
      reteta: [...(prev.reteta || []), newRow]
    }));
  };

  const handleUpdateRecipeLine = (index, field, val) => {
    setFormOrder(prev => {
      const list = [...(prev.reteta || [])];
      const item = { ...list[index], [field]: val };
      if (field === "bxnorma" || field === "luat") {
        const norma = Number(item.bxnorma) || 0;
        const luat = Number(item.luat) || 0;
        item.bxrestqty = Math.max(0, norma - luat);
      }
      list[index] = item;
      return { ...prev, reteta: list };
    });
  };

  const handleRemoveRecipeLine = (index) => {
    setFormOrder(prev => {
      const list = [...(prev.reteta || [])];
      list.splice(index, 1);
      return { ...prev, reteta: list };
    });
  };

  const handleOpenPartPickerForRecipe = (idx) => {
    setActiveRecipeLineIdx(idx);
    setStocPickerOpen(true);
  };

  const handlePickPartForRecipe = (selectedPart) => {
    if (activeRecipeLineIdx !== null && selectedPart) {
      setFormOrder(prev => {
        const list = [...(prev.reteta || [])];
        const row = { ...list[activeRecipeLineIdx] };
        row.bxpart_id = selectedPart.id;
        row.part_code = selectedPart.raw?.cod || "";
        row.part_name = selectedPart.raw?.denumire || "";
        if (selectedPart.raw?.um_id) {
          row.bxum_id = selectedPart.raw.um_id;
        }
        if (selectedPart.raw?.pret_magazie !== undefined) {
          row.pretvanzare = Number(selectedPart.raw.pret_magazie) || 0.0;
        }
        list[activeRecipeLineIdx] = row;
        return { ...prev, reteta: list };
      });
    }
    setStocPickerOpen(false);
    setActiveRecipeLineIdx(null);
  };

  const handlePickManopera = (selectedManopera) => {
    if (activeManoperaLineIdx !== null && selectedManopera) {
      setFormOrder((prev) => {
        const list = [...(prev.manopere || [])];
        const row = { ...list[activeManoperaLineIdx] };
        
        row.bxmanoperacod_id = selectedManopera.id;
        row.bxmanoperacod = selectedManopera.code;
        row.bxspecificatii = selectedManopera.name || "";
        
        Object.assign(row, applyLaborNorm(row, selectedManopera, tarifSkill1, tarifSkill2));

        list[activeManoperaLineIdx] = row;
        return { ...prev, manopere: list };
      });
    }
    setManoperaPickerOpen(false);
    setActiveManoperaLineIdx(null);
  };

  // Save internal order modifications
  const handleSaveInternalOrder = async () => {
    // Validate recipe lines
    if (formOrder.reteta && formOrder.reteta.length > 0) {
      for (let i = 0; i < formOrder.reteta.length; i++) {
        const r = formOrder.reteta[i];
        if (!r.bxpart_id || Number(r.bxpart_id) <= 0) {
          toast.error(`Linia ${i + 1} din Rețetă: Vă rugăm să alegeți un produs/material din stoc.`);
          return;
        }
        if (Number(r.bxnorma) <= 0) {
          toast.error(`Linia ${i + 1} din Rețetă: Cantitatea (Norma) trebuie să fie mai mare decât 0.`);
          return;
        }
      }
    }

    // Validate labor lines
    if (formOrder.manopere && formOrder.manopere.length > 0) {
      for (let i = 0; i < formOrder.manopere.length; i++) {
        const m = formOrder.manopere[i];
        if (!m.bxmanoperacod_id || Number(m.bxmanoperacod_id) <= 0) {
          toast.error(`Linia ${i + 1} din Manoperă: Vă rugăm să alegeți un cod de manoperă.`);
          return;
        }
        if (Number(m.bxqty_manopera) <= 0) {
          toast.error(`Linia ${i + 1} din Manoperă: Cantitatea de manoperă trebuie să fie mai mare decât 0.`);
          return;
        }
      }
    }

    setSavingInternalOrder(true);
    try {
      const payload = {
        bxnumar_comanda: formOrder.bxnumar_comanda,
        bxdescr: formOrder.bxdescr,
        bxcustomer_id: formOrder.bxcustomer_id,
        bxdata_comanda: formOrder.bxdata_comanda,
        bxlvalue: Number(formOrder.bxlvalue) || 0.0,
        bxunitate_id: formOrder.bxunitate_id,
        tipcomanda_id: formOrder.tipcomanda_id,
        bxsasiu: formOrder.bxsasiu,
        bxnumar: formOrder.bxnumar,
        bxkm: formOrder.bxkm,
        bxdovada: formOrder.bxdovada,
        bxpart_id: formOrder.bxpart_id,
        bxcantitate: Number(formOrder.bxcantitate) || 0.0,
        bxprice: Number(formOrder.bxprice) || 0.0,
        bxcant_ramas: Number(formOrder.bxcant_ramas) || 0.0,
        
        manopere: (formOrder.manopere || []).map(m => ({
          bxid: m.bxid,
          bxdata_manopera: m.bxdata_manopera,
          bxqty_manopera: Number(m.bxqty_manopera) || 0.0,
          bxqty_manopera1: Number(m.bxqty_manopera1) || 0.0,
          bxtotal_manopera: Number(m.bxtotal_manopera) || 0.0,
          bxpret_manopera: Number(m.bxpret_manopera) || 0.0,
          detalii_angajati: (m.detalii_angajati || []).map(d => ({ bxangajat_id: d.bxangajat_id })),
          bxspecificatii: m.bxspecificatii || "",
          bxcentrucost_id: Number(m.bxcentrucost_id) || 1,
          bxmanoperacod_id: Number(m.bxmanoperacod_id) || 1,
          bxunitate_id: Number(m.bxunitate_id) || 1,
          bxtip: Number(m.bxtip) || 1,
          bxtiptarif: m.bxtiptarif !== undefined ? Number(m.bxtiptarif) : null,
          bxremiza: m.bxremiza !== undefined ? Number(m.bxremiza) : null,
          qty_executat: m.qty_executat !== undefined ? Number(m.qty_executat) : null,
        })),
        
        reteta: (formOrder.reteta || []).map(r => ({
          bxid: r.bxid,
          bxum_id: Number(r.bxum_id),
          bxpart_id: Number(r.bxpart_id),
          bxnorma: Number(r.bxnorma) || 0.0,
          deluat: Number(r.deluat) || 0.0,
          luat: Number(r.luat) || 0.0,
          bxrectype: Number(r.bxrectype) || 1,
          bxegal: Number(r.bxegal) || 1,
          bxrestqty: Number(r.bxrestqty) || 0.0,
          pretvanzare: Number(r.pretvanzare) || 0.0,
          qty_proforma: Number(r.qty_proforma) || 0.0,
          bxremiza: Number(r.bxremiza) || 0.0,
          bxcomtr_id: r.bxcomtr_id,
          inchid_fortat: Number(r.inchid_fortat) || 0,
          data_scadere: r.data_scadere || null
        }))
      };
      
      const result = await authFetch(`/comenzi/interna/${formOrder.bxid}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      
      toast.success("Comanda internă a fost salvată cu succes!");
      setSelectedInternalOrder(result);
      setFormOrder(result);
      fetchList();
    } catch (err) {
      toast.error(err.message || "Eroare la salvarea comenzii interne.");
    } finally {
      setSavingInternalOrder(false);
    }
  };

  // Stock decrement logic
  const handleScadeStoc = async () => {
    if (!window.confirm("Sigur doriți să realizați scăderea din stoc (FIFO) pentru materialele din rețetă cu cantitatea 'De luat' > 0? Această operațiune va genera un bon de consum.")) {
      return;
    }
    setIsScading(true);
    try {
      const result = await authFetch(`/comenzi/interna/${formOrder.bxid}/scade-stoc`, {
        method: "POST"
      });
      toast.success("Scăderea din stoc a fost realizată cu succes! S-a generat un bon de consum.");
      setSelectedInternalOrder(result);
      setFormOrder(result);
      fetchList();
    } catch (err) {
      toast.error(err.message || "Eroare la scăderea din stoc.");
    } finally {
      setIsScading(false);
    }
  };

  // Get color badge based on status code
  const getStatusBadge = (code, descr) => {
    let bg = "secondary";
    if (code === "PRE") bg = "info";
    else if (code === "REZOLVAT") bg = "success";
    else if (code === "SOSIT") bg = "primary";
    else if (code === "COMANDAT") bg = "warning";
    else if (code === "FACTURAT") bg = "success";
    else if (code === "PARTIAL_FACT") bg = "warning";
    else if (code === "STORNAT") bg = "danger";

    return (
      <Badge bg={bg} className="px-2 py-1 text-uppercase">
        {descr || code}
      </Badge>
    );
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const from = totalCount ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalCount, (currentPage + 1) * itemsPerPage);

  return (
    <Container fluid className="px-4 py-4">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h2 className="fw-bold text-dark mb-1">Comenzi Clienți (Simaus)</h2>
          <p className="text-muted mb-0">Urmărire și gestionare linii de comenzi clienți, alocări piese și generare comenzi interne.</p>
        </div>
        <div>
          <Button as={Link} to="/comenzi/client/new" variant="primary" className="fw-semibold">
            + Comandă Nouă
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <Card className="shadow-sm border-0 mb-4 bg-light">
        <Card.Body className="p-3">
          <Form onSubmit={handleSearchSubmit}>
            <Row className="g-3 align-items-end">
              <Col lg={4} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">Caută</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Nr. comandă, client, cod piesă, WO, șasiu..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              
              <Col lg={3} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">Filtru Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(0);
                    }}
                  >
                    <option value="">-- Toate statusurile --</option>
                    {statuses.map((s) => (
                      <option key={s.bxid} value={s.bxid}>
                        {s.bxdescr} ({s.bxcod})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col lg={2} md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">Rânduri</Form.Label>
                  <Form.Select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(0);
                    }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n} pe pagină
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col lg={3} md={8} className="d-flex gap-2">
                <Button type="submit" variant="primary" className="w-100 fw-semibold">
                  Filtrează
                </Button>
                <Button variant="outline-secondary" onClick={handleClearFilters} className="w-100 fw-semibold">
                  Resetează
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" className="shadow-sm">{error}</Alert>}

      {/* Grid Table */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom">
            <Form.Check type="switch" id="client-order-lines-view" label="Detalii pe produse"
              checked={showOrderLines} onChange={e => {setShowOrderLines(e.target.checked);setCurrentPage(0);}} />
            <span className="text-muted small fw-semibold">
              {totalCount ? `Afișare înregistrări: ${from}-${to} din ${totalCount}` : "Nu s-au găsit înregistrări"}
            </span>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" variant="primary" className="my-3" />
              <p className="text-muted mb-0">Se încarcă datele...</p>
            </div>
          ) : (
            <div className="table-responsive">
              {showOrderLines ? (<Table hover striped bordered className="align-middle mb-0 text-nowrap" size="sm">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center" style={{ width: "40px" }}>No</th>
                    <th style={{ width: "50px" }}>Id</th>
                    <th style={{ width: "50px" }}>Valuta</th>
                    <th style={{ width: "80px" }}>Numar</th>
                    <th style={{ width: "90px" }}>Data</th>
                    <th style={{ width: "80px" }}>WO</th>
                    <th style={{ width: "150px" }}>Client</th>
                    <th style={{ width: "120px" }}>Cod Piesă</th>
                    <th style={{ width: "180px" }}>Denumire Piesă</th>
                    <th className="text-end" style={{ width: "80px" }}>Preț</th>
                    <th style={{ width: "100px" }}>Comision</th>
                    <th style={{ width: "120px" }}>Identificare</th>
                    <th style={{ width: "150px" }}>Livrare (Descr)</th>
                    <th style={{ width: "100px" }}>Status</th>
                    <th className="text-center" style={{ width: "60px" }}>Cerut</th>
                    <th className="text-center" style={{ width: "60px" }}>Rezervat</th>
                    <th className="text-center" style={{ width: "60px" }}>Predat</th>
                    <th className="text-center" style={{ width: "60px" }}>Facturat</th>
                    <th className="text-center" style={{ width: "60px" }}>Ramas</th>
                    <th style={{ width: "95px" }}>Termen Liv.</th>
                    <th className="text-end" style={{ width: "100px" }}>Valoare</th>
                    <th className="text-center" style={{ width: "130px" }}>Comandă Internă</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const rowNumber = currentPage * itemsPerPage + idx + 1;
                    return (
                      <tr key={it.bxid}>
                        <td className="text-center fw-semibold text-muted bg-light">{rowNumber}</td>
                        <td className="fw-mono small">{it.is_header_only ? "—" : it.bxid}</td>
                        <td className="text-center">{it.valuta_code || "-"}</td>
                        <td className="fw-semibold">
                          <Link to={`/comenzi/client/edit/${it.bxcomtop_id}`} className="text-decoration-underline text-primary">
                            {it.bxnumar_comanda || "-"}
                          </Link>
                        </td>
                        <td>{formatDateRo(it.bxdata_comanda)}</td>
                        <td>{it.termen_livrare2 || "-"}</td>
                        <td className="text-truncate" style={{ maxWidth: "200px" }} title={it.customer_name}>
                          {it.customer_name || "-"}
                        </td>
                        <td className="fw-bold">{it.part_code || "-"}</td>
                        <td className="text-truncate" style={{ maxWidth: "220px" }} title={it.part_name}>
                          {it.is_header_only ? "Fără piese sau lucrări" : (it.part_name || "-")}
                        </td>
                        <td className="text-end fw-mono">{it.is_header_only ? "—" : Number(it.bxpret_vinzare || 0).toFixed(2)}</td>
                        <td>{it.comision || "-"}</td>
                        <td className="text-muted">{it.bxsasiu || "-"}</td>
                        <td className="text-truncate" style={{ maxWidth: "200px" }} title={it.parent_descr}>
                          {it.parent_descr || "-"}
                        </td>
                        <td className="text-center">{getStatusBadge(it.status_code, it.status_descr)}</td>
                        <td className="text-center fw-semibold">{it.bxqtyrequest}</td>
                        <td className="text-center text-primary">{it.bxqtyrez}</td>
                        <td className="text-center text-success">{it.predat}</td>
                        <td className="text-center text-info">{it.bxfacturat}</td>
                        <td className="text-center text-danger fw-semibold">{it.ramas}</td>
                        <td>{formatDateRo(it.termen_livrare1)}</td>
                        <td className="text-end fw-mono fw-bold">{it.is_header_only ? "—" : Number(it.bxlamount || 0).toFixed(2)}</td>
                        <td className="text-center">
                          {it.is_header_only ? (
                            <Link to={`/comenzi/client/edit/${it.bxcomtop_id}`} className="btn btn-outline-primary btn-sm">
                              Deschide comanda
                            </Link>
                          ) : it.bxcomanda_id ? (
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 fw-semibold text-decoration-underline"
                                onClick={() => handleViewInternalOrder(it.bxcomanda_id)}
                              >
                                {it.bxcomanda_numar || `Deviz: ${it.bxcomanda_id}`}
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="py-0 px-1 border-0"
                                title="Șterge legătura (dacă devizul a fost șters)"
                                onClick={async () => {
                                  if(window.confirm("Ștergi legătura cu acest deviz/comandă internă veche?")) {
                                    try {
                                      await authFetch(`/comenzi/client/linie/${it.bxid}/unlink-interna`, { method: "POST" });
                                      toast.success("Legătura a fost ștearsă!");
                                      fetchList();
                                    } catch(e) {
                                      toast.error("Eroare la ștergerea legăturii");
                                    }
                                  }
                                }}
                              >
                                &times;
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex flex-column gap-1 align-items-center">
                                <Button
                                  variant="success"
                                  size="sm"
                                  className="py-0 px-2 fw-semibold"
                                  style={{ fontSize: "0.75rem" }}
                                  disabled={generatingProdId === it.bxid}
                                  onClick={() => handleGenerateProdOrder(it.bxid)}
                                >
                                  {generatingProdId === it.bxid ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                  + Bon Producție
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="py-0 px-2"
                                  style={{ fontSize: "0.75rem" }}
                                  disabled={generatingId === it.bxid}
                                  onClick={() => handleGenerateInternalOrder(it.bxid)}
                                >
                                  {generatingId === it.bxid ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                  + Deviz Auto
                                </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={22} className="text-center text-muted py-5 fs-5 bg-light">
                        Nu s-au găsit comenzi clienți. Modificați filtrele sau verificați conexiunea.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>) : (
                <ClientOrderSummaryTable items={items} currentPage={currentPage}
                  itemsPerPage={itemsPerPage} formatDateRo={formatDateRo} appVariant={appVariant} />
              )}
            </div>
          )}
        </Card.Body>

        <Card.Footer className="bg-white border-top py-3">
          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center mb-0"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            renderOnZeroPageCount={null}
          />
        </Card.Footer>
      </Card>

      {/* Internal Order Details Modal */}
      <Modal
        show={showInternalOrderModal}
        onHide={() => {
          setShowInternalOrderModal(false);
          setSelectedInternalOrder(null);
          setFormOrder(null);
        }}
        backdrop="static"
        keyboard={false}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="fw-bold">
            {formOrder ? `Editare Comandă Internă #${formOrder.bxnumar_comanda}` : "Detalii Comandă Internă"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {loadingInternalOrder ? (
            <div className="py-5 text-center">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Se încarcă detaliile...</p>
            </div>
          ) : formOrder ? (
            <Tabs
              activeKey={modalTab}
              onSelect={(k) => setModalTab(k)}
              className="mb-4"
            >
              <Tab eventKey="general" title="General">
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Număr Comandă</Form.Label>
                      <Form.Control type="text" value={formOrder.bxnumar_comanda || ""} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Data Comandă</Form.Label>
                      <Form.Control type="text" value={formatDateRo(formOrder.bxdata_comanda) || ""} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Client</Form.Label>
                      <Form.Control type="text" value={formOrder.customer_name || ""} disabled />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Cod Piesă fabricat</Form.Label>
                      <Form.Control type="text" value={formOrder.part_code || ""} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Denumire Piesă</Form.Label>
                      <Form.Control type="text" value={formOrder.part_name || ""} disabled />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Serie Șasiu</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formOrder.bxsasiu || ""} 
                        onChange={(e) => handleHeaderChange("bxsasiu", e.target.value)} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Număr</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formOrder.bxnumar || ""} 
                        onChange={(e) => handleHeaderChange("bxnumar", e.target.value)} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Km</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formOrder.bxkm || ""} 
                        onChange={(e) => handleHeaderChange("bxkm", e.target.value)} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Dovadă</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formOrder.bxdovada || ""} 
                        onChange={(e) => handleHeaderChange("bxdovada", e.target.value)} 
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Cantitate</Form.Label>
                      <Form.Control 
                        type="number" 
                        step="any"
                        value={formOrder.bxcantitate ?? 0} 
                        onChange={(e) => handleHeaderChange("bxcantitate", Number(e.target.value))} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Preț Unitar</Form.Label>
                      <Form.Control 
                        type="number" 
                        step="any"
                        value={formOrder.bxprice ?? 0} 
                        onChange={(e) => handleHeaderChange("bxprice", Number(e.target.value))} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Valoare (Total)</Form.Label>
                      <Form.Control 
                        type="number" 
                        value={Number(formOrder.bxlvalue ?? 0).toFixed(2)} 
                        disabled 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Tip Comandă</Form.Label>
                      <Form.Select 
                        value={formOrder.tipcomanda_id ?? 2} 
                        onChange={(e) => handleHeaderChange("tipcomanda_id", Number(e.target.value))}
                      >
                        <option value={2}>Domestic (RO)</option>
                        <option value={1}>Export / Extern</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small text-uppercase">Descriere / Observații</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={3} 
                        value={formOrder.bxdescr || ""} 
                        onChange={(e) => handleHeaderChange("bxdescr", e.target.value)} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="manopere" title="Manoperă (Labor)">
                <div className="table-responsive mt-3">
                  <Table bordered size="sm" className="align-middle">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: "180px" }}>Data Manoperă</th>
                        <th style={{ width: "120px" }}>Cod Manoperă</th>
                        <th style={{ width: "90px" }}>Ore normate</th>
                        <th style={{ width: "110px" }}>Tarif lei/oră</th>
                        <th style={{ width: "110px" }}>Total</th>
                        <th>Specificații</th><th>Angajați / repartizare</th>
                        <th style={{ width: "80px" }} className="text-center">Șterge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(formOrder.manopere || []).map((m, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Control
                              type="datetime-local"
                              size="sm"
                              value={formatDateTimeLocal(m.bxdata_manopera)}
                              onChange={(e) => handleUpdateManopera(idx, "bxdata_manopera", e.target.value ? new Date(e.target.value).toISOString() : "")}
                            />
                          </td>
                          <td>
                            <div className="d-flex gap-1 align-items-center">
                              <Form.Control
                                type="text"
                                size="sm"
                                value={m.bxmanoperacod ?? m.bxmanoperacod_id ?? ""}
                                readOnly
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setActiveManoperaLineIdx(idx);
                                  setManoperaPickerOpen(true);
                                }}
                              />
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="py-0 px-2"
                                onClick={() => {
                                  setActiveManoperaLineIdx(idx);
                                  setManoperaPickerOpen(true);
                                }}
                              >
                                🔍
                              </Button>
                            </div>
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              step="any"
                              value={m.bxqty_manopera ?? 0}
                              onChange={(e) => handleUpdateManopera(idx, "bxqty_manopera", Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              step="any"
                              value={m.bxpret_manopera ?? 0}
                              onChange={(e) => handleUpdateManopera(idx, "bxpret_manopera", Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={Number(m.bxtotal_manopera ?? 0).toFixed(2)}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={m.bxspecificatii || ""}
                              onChange={(e) => handleUpdateManopera(idx, "bxspecificatii", e.target.value)}
                              placeholder="Detalii..."
                            />
                          </td>
                          <td><LaborEmployees value={m.detalii_angajati || []} amount={Number(m.bxqty_manopera || 0) * Number(m.bxpret_manopera || 0)} onChange={value => handleUpdateManopera(idx, "detalii_angajati", value)} /></td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveManopera(idx)}
                            >
                              ✕
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {(formOrder.manopere || []).length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center py-3 text-muted bg-light">
                            Nu există manopere adăugate pe această comandă.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table><LaborEmployeeSummary rows={formOrder.manopere || []} />
                  <Button variant="success" size="sm" onClick={handleAddManopera} className="fw-semibold">
                    + Adaugă Manoperă
                  </Button>
                </div>
              </Tab>

              {appVariant === "SIMAUS" && (
                <Tab eventKey="reteta" title="Rețetă (Recipe / Materiale)">
                  <div className="table-responsive mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Button variant="success" size="sm" onClick={() => handleAddRecipeLine(11)} className="fw-semibold">
                        + Adaugă Rând Rețetă
                      </Button>
                      <div className="d-flex gap-2 align-items-center">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          className="fw-bold px-3"
                          disabled={!(formOrder.reteta || []).some(r => r.bxrectype === 11 && r.deluat > 0) || isScading}
                          onClick={handleScadeStoc}
                        >
                          {isScading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Se scade...
                            </>
                          ) : (
                            "Scade din Stoc (FIFO)"
                          )}
                        </Button>
                      </div>
                    </div>

                    <Table bordered size="sm" className="align-middle">
                      <thead className="table-dark">
                        <tr>
                          <th style={{ width: "135px" }}>Dată Scădere</th>
                          <th>Produs / Material</th>
                          <th style={{ width: "120px" }}>U.M.</th>
                          <th style={{ width: "90px" }}>Norma</th>
                          <th style={{ width: "90px" }}>De luat</th>
                          <th style={{ width: "90px" }}>Luat</th>
                          <th style={{ width: "90px" }}>Rămas</th>
                          <th style={{ width: "110px" }}>Preț Vânzare</th>
                          <th style={{ width: "110px" }}>Tip Rec</th>
                          <th style={{ width: "80px" }} className="text-center">Șterge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formOrder.reteta || []).map((r, idx) => {
                          if (r.bxrectype !== 11) return null;
                          return (
                            <tr key={idx}>
                              <td>
                                <Form.Control
                                  type="date"
                                  size="sm"
                                  value={r.data_scadere || ""}
                                  onChange={(e) => handleUpdateRecipeLine(idx, "data_scadere", e.target.value)}
                                />
                              </td>
                              <td>
                                <div className="d-flex gap-2 align-items-center">
                                  <span className="fw-semibold text-dark">
                                    {r.part_code ? `${r.part_code} • ${r.part_name}` : "(niciun produs selectat)"}
                                  </span>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="py-0 px-2 ms-auto"
                                    onClick={() => handleOpenPartPickerForRecipe(idx)}
                                  >
                                    Caută
                                  </Button>
                                </div>
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={r.bxum_id}
                                  onChange={(e) => handleUpdateRecipeLine(idx, "bxum_id", Number(e.target.value))}
                                >
                                  {ums.map((opt) => (
                                    <option key={opt.id} value={opt.id}>
                                      {opt.cod}
                                    </option>
                                  ))}
                                </Form.Select>
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  step="any"
                                  value={r.bxnorma ?? 0}
                                  onChange={(e) => handleUpdateRecipeLine(idx, "bxnorma", Number(e.target.value))}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  step="any"
                                  value={r.deluat ?? 0}
                                  onChange={(e) => handleUpdateRecipeLine(idx, "deluat", Number(e.target.value))}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={r.luat ?? 0}
                                  disabled
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={Number(r.bxrestqty ?? 0).toFixed(2)}
                                  disabled
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  step="any"
                                  value={r.pretvanzare ?? 0}
                                  onChange={(e) => handleUpdateRecipeLine(idx, "pretvanzare", Number(e.target.value))}
                                />
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={r.bxrectype ?? 1}
                                  onChange={(e) => handleUpdateRecipeLine(idx, "bxrectype", Number(e.target.value))}
                                >
                                  <option value={11}>11 (Rețetă)</option>
                                  <option value={1}>1 (Material)</option>
                                  <option value={2}>2 (Obiect)</option>
                                </Form.Select>
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveRecipeLine(idx)}
                                >
                                  ✕
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                        {!(formOrder.reteta || []).some(r => r.bxrectype === 11) && (
                          <tr>
                            <td colSpan={10} className="text-center py-5 text-muted bg-light">
                              Nu există materiale adăugate în rețetă.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab>
              )}

              <Tab eventKey="materiale_direct" title={appVariant === "SERVICE_AUTO" ? "Piese / Materiale" : "Piese / Materiale (Service)"}>
                <div className="table-responsive mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button variant="success" size="sm" onClick={() => handleAddRecipeLine(1)} className="fw-semibold">
                      + Adaugă Piese / Materiale
                    </Button>
                  </div>

                  <Table bordered size="sm" className="align-middle">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: "135px" }}>Dată Scădere</th>
                        <th>Produs / Material</th>
                        <th style={{ width: "120px" }}>U.M.</th>
                        <th style={{ width: "120px" }}>Cantitate</th>
                        <th style={{ width: "130px" }}>Preț Vânzare</th>
                        <th style={{ width: "130px" }}>Tip Rec</th>
                        <th style={{ width: "80px" }} className="text-center">Șterge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(formOrder.reteta || []).map((r, idx) => {
                        if (r.bxrectype === 11) return null;
                        return (
                          <tr key={idx}>
                            <td>
                              <Form.Control
                                type="date"
                                size="sm"
                                value={r.data_scadere || ""}
                                onChange={(e) => handleUpdateRecipeLine(idx, "data_scadere", e.target.value)}
                              />
                            </td>
                            <td>
                              <div className="d-flex gap-2 align-items-center">
                                <span className="fw-semibold text-dark">
                                  {r.part_code ? `${r.part_code} • ${r.part_name}` : "(niciun produs selectat)"}
                                </span>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="py-0 px-2 ms-auto"
                                  onClick={() => handleOpenPartPickerForRecipe(idx)}
                                >
                                  Caută
                                </Button>
                              </div>
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={r.bxum_id}
                                onChange={(e) => handleUpdateRecipeLine(idx, "bxum_id", Number(e.target.value))}
                              >
                                {ums.map((opt) => (
                                  <option key={opt.id} value={opt.id}>
                                    {opt.cod}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                step="any"
                                value={r.bxnorma ?? 0}
                                onChange={(e) => handleUpdateRecipeLine(idx, "bxnorma", Number(e.target.value))}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                step="any"
                                value={r.pretvanzare ?? 0}
                                onChange={(e) => handleUpdateRecipeLine(idx, "pretvanzare", Number(e.target.value))}
                              />
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={r.bxrectype ?? 1}
                                onChange={(e) => handleUpdateRecipeLine(idx, "bxrectype", Number(e.target.value))}
                              >
                                <option value={1}>1 (Material)</option>
                                <option value={2}>2 (Obiect)</option>
                              </Form.Select>
                            </td>
                            <td className="text-center">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveRecipeLine(idx)}
                              >
                                ✕
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {!(formOrder.reteta || []).some(r => r.bxrectype !== 11) && (
                        <tr>
                          <td colSpan={7} className="text-center py-5 text-muted bg-light">
                            Nu există piese sau materiale adăugate direct.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Tab>
            </Tabs>
          ) : (
            <Alert variant="warning">Nu s-au putut încărca datele...</Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setShowInternalOrderModal(false)} disabled={savingInternalOrder}>
            Închide
          </Button>
          {formOrder && (
            <Button variant="primary" onClick={handleSaveInternalOrder} disabled={savingInternalOrder}>
              {savingInternalOrder ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Se salvează...
                </>
              ) : (
                "Salvează modificări"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Stoc Curent Picker Modal */}
      <StocCurentPickerModal
        show={stocPickerOpen}
        onHide={() => {
          setStocPickerOpen(false);
          setActiveRecipeLineIdx(null);
        }}
        onPick={handlePickPartForRecipe}
        gestiuneId={1}
      />

      {/* Manopera Picker Modal */}
      <ManoperaPickerModal
        show={manoperaPickerOpen}
        onHide={() => {
          setManoperaPickerOpen(false);
          setActiveManoperaLineIdx(null);
        }}
        onPick={handlePickManopera}
      />
    </Container>
  );
}
